// Service centralisant les appels backend SCOR (pieces justificatives / numerisation).
// Porte les fenetres desktop frmPJScor (PJ d'une FSE) et frmChoixOrdo (biblio ordonnances
// du patient). La NUMERISATION se fait cote navigateur : on acquiert le fichier (PDF de
// preference), on l'envoie en base64 PUR (sans prefixe data:), le backend fait le reste
// (PDF/A, typage, lot, chiffrement, envoi CNA).
// Contrat backend : docs/plans/2026-07-07-scor-pieces-jointes-fse-design.md
//                   + ../BackEndFInal/docs/SCOR-FRONT-HANDOFF.md
import config from "../config.js";
import RequestManager from "./request.js";

export default class Scor {

    // --- Bibliotheque d'ordonnances DU PATIENT (reutilisable entre FSE) -------

    // -> { success, ordonnances: [ { Nom, Date, TailleKo } ] }
    static listerOrdonnances(identPatient) {
        const url = `${config.urlBackEnd}Scor/ordonnances/${encodeURIComponent(identPatient)}`;
        return RequestManager.get(url);
    }

    // body { FichierBase64, NomFichier } -> { success, nom }
    static ajouterOrdonnance(identPatient, payload) {
        const url = `${config.urlBackEnd}Scor/ordonnances/${encodeURIComponent(identPatient)}`;
        return RequestManager.post(url, payload);
    }

    // -> { success, nom, contenuBase64 }
    static contenuOrdonnance(identPatient, nom) {
        const url = `${config.urlBackEnd}Scor/ordonnances/${encodeURIComponent(identPatient)}/contenu?nom=${encodeURIComponent(nom)}`;
        return RequestManager.get(url);
    }

    static supprimerOrdonnance(identPatient, nom) {
        const url = `${config.urlBackEnd}Scor/ordonnances/${encodeURIComponent(identPatient)}?nom=${encodeURIComponent(nom)}`;
        return RequestManager.delete(url);
    }

    // --- Pieces jointes D'UNE FSE (clef = noFse = SDES_CODE) -------------------

    // -> { success, pieces: [ { Nom, Nature, Date, TailleKo } ], statut: {...} }
    static listerPieces(noFse) {
        const url = `${config.urlBackEnd}Scor/fse/${encodeURIComponent(noFse)}/pieces`;
        return RequestManager.get(url);
    }

    // body { FichierBase64, NomFichier, Nature } -> { success, nom, statut }
    static ajouterPiece(noFse, payload) {
        const url = `${config.urlBackEnd}Scor/fse/${encodeURIComponent(noFse)}/pieces`;
        return RequestManager.post(url, payload);
    }

    // body { IdentPatient, Nom } -> { success, nom, statut }  (attache une ordo de la biblio patient)
    static attacherDepuisPatient(noFse, payload) {
        const url = `${config.urlBackEnd}Scor/fse/${encodeURIComponent(noFse)}/pieces/depuisPatient`;
        return RequestManager.post(url, payload);
    }

    // -> { success, nom, contenuBase64 }
    static contenuPiece(noFse, nom) {
        const url = `${config.urlBackEnd}Scor/fse/${encodeURIComponent(noFse)}/pieces/contenu?nom=${encodeURIComponent(nom)}`;
        return RequestManager.get(url);
    }

    // -> { success, statut }
    static supprimerPiece(noFse, nom) {
        const url = `${config.urlBackEnd}Scor/fse/${encodeURIComponent(noFse)}/pieces?nom=${encodeURIComponent(nom)}`;
        return RequestManager.delete(url);
    }

    // -> { success, statut }
    static statut(noFse) {
        const url = `${config.urlBackEnd}Scor/fse/${encodeURIComponent(noFse)}/statut`;
        return RequestManager.get(url);
    }

    // --- Contexte post-enregistrement (question ordonnance = frmChoixOrdo) -----

    // Apres FSE/enregistrerFse : n° de FSE crees + identifiant patient (pour la biblio).
    // -> { success, noFses: [int], identPatient: int, copiePapierDegradeRequise: bool }
    static contexteEnregistrement() {
        const url = `${config.urlBackEnd}Scor/contexteEnregistrement`;
        return RequestManager.get(url);
    }

    // --- Transmission des lots au CNA (backend PAS finalise, cf handoff §5bis) --

    static creerLot(numLot) {
        const url = `${config.urlBackEnd}Scor/lot/${encodeURIComponent(numLot)}/creer`;
        return RequestManager.post(url, {});
    }

    static envoyer(nocps) {
        const url = `${config.urlBackEnd}Scor/envoyer`;
        return RequestManager.post(url, { Nocps: nocps });
    }

    // --- Helpers acquisition navigateur --------------------------------------

    // Encode un File/Blob en base64 PUR (sans le prefixe "data:...;base64,").
    static fichierEnBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const res = String(reader.result || "");
                const virgule = res.indexOf(",");
                resolve(virgule >= 0 ? res.slice(virgule + 1) : res);
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    }

    // Ouvre un contenu base64 (PDF/image) dans un nouvel onglet pour apercu/telechargement.
    static apercuBase64(contenuBase64, nom) {
        const mime = /\.png$|\.jpe?g$/i.test(nom || "") ? "image/*" : "application/pdf";
        const octets = atob(contenuBase64);
        const buf = new Uint8Array(octets.length);
        for (let i = 0; i < octets.length; i++) buf[i] = octets.charCodeAt(i);
        const blob = new Blob([buf], { type: mime });
        const href = URL.createObjectURL(blob);
        window.open(href, "_blank");
        // Liberation differee : laisse le temps a l'onglet d'ouvrir le blob.
        setTimeout(() => URL.revokeObjectURL(href), 60000);
    }

    // Statut SCOR normalise (le backend peut sortir en PascalCase) -> objet stable.
    // Code : -1 erreur, 0 non necessaires, 1 non presentes, 2 partiellement, 3 presentes.
    static normaliserStatut(s) {
        if (!s) return null;
        return {
            code: s.Code ?? s.code ?? 0,
            statut: s.Statut ?? s.statut ?? "",
            raisonManque: s.RaisonManque ?? s.raisonManque ?? "",
            copiePapierDegradeRequise: s.CopiePapierDegradeRequise ?? s.copiePapierDegradeRequise ?? false,
        };
    }
}
